import React, { useEffect, useState } from 'react';
import {
    createColumnHelper,
    flexRender,
    getCoreRowModel,
    getSortedRowModel,
    SortingState,
    useReactTable,
} from '@tanstack/react-table';
import { useDisclosure } from '@chakra-ui/hooks';

import { MdSend, MdSimCardDownload } from 'react-icons/md';
import Card from 'components/card';
import {
    addTransaction,
    downloadInvoice,
    generateCreditNote,
} from 'interfaces/invoice';
import InvoiceMenu from './invoiceMenu';
import invoiceImg from 'assets/img/invoice.png';
import CreditNoteModal from './CreditNoteModal';
import EmailsHistoryModal from './emailsHistoryModal';
import CreditNotePartialModal from './CreditNotePartialModal';
import MarkAsPaidModal from 'views/invoicing/list-invoices/components/MarkAsPaidModal';

const columnHelper = createColumnHelper<any>();

const getDiffDays = (dueDate: string | undefined): number => {
    if (!dueDate) return -Infinity;
    const now = new Date();
    const due = new Date(dueDate);
    return Math.floor((now.getTime() - due.getTime()) / (1000 * 60 * 60 * 24));
};

export default function InvoicesTable(props: {
    tableData: any;
    onOpen: any;
    onEmailSenderModalOpen: any;
    invoicesMeta: any;
    onFilter: any;
    onInvoiceUpdated?: (invoice: any) => void;
    dateFrom?: string;
    dateTo?: string;
    onDateChange?: (from: string, to: string) => void;
    onSearchChange?: (search: string) => void;
}) {
    const {
        onEmailSenderModalOpen,
        onFilter,
        onInvoiceUpdated,
        invoicesMeta,
        dateFrom: dateFromProp = '',
        dateTo: dateToProp = '',
        onDateChange,
        onSearchChange,
    } = props;

    const { isOpen, onOpen: onOpenCreditNote, onClose } = useDisclosure();
    const {
        isOpen: isOpenCreditNotePartial,
        onOpen: onOpenCreditNotePartial,
        onClose: onCloseCreditNotePartial,
    } = useDisclosure();
    const {
        isOpen: isOpenMarkAsPaid,
        onOpen: onOpenMarkAsPaid,
        onClose: OnCloseMarkAsPaid,
    } = useDisclosure();
    const {
        isOpen: isOpenEmailsHistory,
        onOpen: onOpenEmailsHistory,
        onClose: OnCloseEmailHistory,
    } = useDisclosure();

    const [sorting, setSorting] = React.useState<SortingState>([]);
    const [data, setData] = React.useState<any[]>(() => []);
    const [search, setSearch] = React.useState('');
    const [dateFrom, setDateFrom] = React.useState(dateFromProp);
    const [dateTo, setDateTo] = React.useState(dateToProp);

    const searchDebounceRef = React.useRef<ReturnType<typeof setTimeout>>(null);
    const handleSearchChange = (value: string) => {
        setSearch(value);
        if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
        searchDebounceRef.current = setTimeout(() => {
            if (onSearchChange) onSearchChange(value);
        }, 400);
    };

    const handleDateFromChange = (value: string) => {
        setDateFrom(value);
        if (onDateChange) onDateChange(value, dateTo);
    };

    const handleDateToChange = (value: string) => {
        setDateTo(value);
        if (onDateChange) onDateChange(dateFrom, value);
    };

    const [creditNoteToCreate, setCreditNoteToCreate] = useState(null);
    const [invoiceToMarkAsPaid, setInvoiceToMarkAsPaid] = useState(null);

    const filteredData = React.useMemo(() => {
        if (!search) return data;
        return data.filter((invoice: any) =>
            invoice.customer?.toLowerCase().includes(search.toLowerCase()),
        );
    }, [data, search]);

    useEffect(() => {
        const tableData = props.tableData.map((invoice: any) => {
            const date = invoice.invoicedAt || invoice.updatedAt;
            return {
                ...invoice,
                customer: invoice.customer.company,
                n: {
                    invoiceNumber: invoice.invoiceNumber,
                    date: date.split('T')[0].split('-').reverse().join('/'),
                },
                title: `${invoice.invoiceLines.length} commandes`,
            };
        });
        setData(tableData);
    }, [props.tableData]);

    const fetchInvoice = async (id: string) => {
        try {
            const blob = await downloadInvoice(id);
            if (!blob) return;
            const invoice = data.find((inv: any) => inv._id === id);
            const link = document.createElement('a');
            link.href = window.URL.createObjectURL(blob);
            link.download = `${invoice?.to.company}-${invoice?.invoiceNumber}.pdf`;
            link.click();
        } catch (error) {
            console.error('Error generating PDF:', error);
        }
    };

    const addInvoiceTransaction = async (invoiceId: string, transaction: any) => {
        const res = await addTransaction(invoiceId, transaction);
        if (!res || !res.data) return;
        OnCloseMarkAsPaid();
        if (onInvoiceUpdated) onInvoiceUpdated(res.data);
    };

    const isOpenEmailsHistoryModal = (invoice: any) => {
        setInvoiceToMarkAsPaid(invoice);
        onOpenEmailsHistory();
    };

    const openTransactionModal = async (selectedInvoice: any) => {
        setInvoiceToMarkAsPaid(selectedInvoice);
        onOpenMarkAsPaid();
    };

    const openCreditNoteModal = async (invoiceId: string) => {
        setCreditNoteToCreate(invoiceId);
        onOpenCreditNote();
    };

    const createCreditNote = async (creditNoteAmount: number = null, vat: number = null) => {
        const res = await generateCreditNote(creditNoteToCreate, creditNoteAmount, vat);
        if (!res) return;
        setCreditNoteToCreate(null);
        onClose();
        onCloseCreditNotePartial();
        onFilter(null, 'all');
    };

    const columns = [
        columnHelper.accessor('n', {
            id: 'n',
            header: () => (
                <p className="text-xs font-semibold uppercase tracking-wide text-gray-400 dark:text-gray-500">
                    N° Facture
                </p>
            ),
            cell: (info) => (
                <div className="flex flex-col gap-0.5">
                    <p className="text-xs text-gray-400 dark:text-gray-500">
                        #{info.getValue()['invoiceNumber']}
                    </p>
                    <p className="text-sm font-semibold text-navy-700 dark:text-white">
                        {info.getValue()['date']}
                    </p>
                </div>
            ),
        }),
        columnHelper.accessor('customer', {
            id: 'customer',
            header: () => (
                <p className="text-xs font-semibold uppercase tracking-wide text-gray-400 dark:text-gray-500">
                    Client
                </p>
            ),
            cell: (info) => (
                <p className="text-sm font-semibold text-navy-700 dark:text-white">
                    {info.getValue()}
                </p>
            ),
        }),
        columnHelper.accessor('leftToPay', {
            id: 'leftToPay',
            header: () => (
                <p className="text-xs font-semibold uppercase tracking-wide text-gray-400 dark:text-gray-500">
                    Reste à payer
                </p>
            ),
            cell: (info) => (
                <p className="text-sm font-bold tabular-nums text-navy-700 dark:text-white">
                    {info.getValue() >= 0 ? info.getValue().toFixed(2) : '-- --'} €
                </p>
            ),
        }),
        columnHelper.accessor('dueDate', {
            id: 'dueDate',
            header: () => (
                <p className="text-xs font-semibold uppercase tracking-wide text-gray-400 dark:text-gray-500">
                    Retard
                </p>
            ),
            cell: (info) => {
                const dueDate = info.getValue();
                if (!dueDate) return <span className="text-sm text-gray-300">—</span>;
                const diffDays = getDiffDays(dueDate as string);
                if (diffDays <= 0) {
                    return (
                        <span className="inline-flex items-center rounded-full bg-green-50 px-2.5 py-0.5 text-xs font-semibold text-green-700 ring-1 ring-inset ring-green-200">
                            À temps
                        </span>
                    );
                }
                return (
                    <span
                        className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold ring-1 ring-inset ${
                            diffDays > 30
                                ? 'bg-red-50 text-red-700 ring-red-200'
                                : 'bg-orange-50 text-orange-700 ring-orange-200'
                        }`}
                    >
                        {diffDays}j de retard
                    </span>
                );
            },
        }),
        columnHelper.accessor('_id', {
            id: '_id',
            header: () => null,
            cell: (info) => (
                <div className="flex items-center gap-0.5">
                    <button
                        title="Télécharger la facture"
                        onClick={() => fetchInvoice(info.getValue())}
                        className="rounded-lg p-1.5 text-gray-400 transition-colors duration-100 hover:bg-lightPrimary hover:text-toola-500"
                    >
                        <MdSimCardDownload className="h-5 w-5" />
                    </button>
                    <button
                        title="Envoyer par mail"
                        onClick={() => onEmailSenderModalOpen(info.getValue())}
                        className="rounded-lg p-1.5 text-gray-400 transition-colors duration-100 hover:bg-lightPrimary hover:text-toola-500"
                    >
                        <MdSend className="h-5 w-5" />
                    </button>
                    <InvoiceMenu
                        disabled={['canceled', 'credit-note'].includes(
                            data.find((inv: any) => inv._id === info.getValue())?.status,
                        )}
                        isLastItem={
                            data.findIndex((inv: any) => inv._id === info.getValue()) === data.length - 1 ||
                            data.findIndex((inv: any) => inv._id === info.getValue()) === data.length - 2
                        }
                        invoice={data.find((inv: any) => inv._id === info.getValue())}
                        openHistory={isOpenEmailsHistoryModal}
                        updateInvoiceStatus={openTransactionModal}
                        createCreditNote={(id: string) => openCreditNoteModal(id)}
                    />
                </div>
            ),
        }),
    ];

    const table = useReactTable({
        data: filteredData,
        columns,
        state: { sorting },
        onSortingChange: setSorting,
        getCoreRowModel: getCoreRowModel(),
        getSortedRowModel: getSortedRowModel(),
        debugTable: false,
    });

    return (
        <>
            <CreditNoteModal
                isOpen={isOpen}
                onClose={onClose}
                generateCreditNote={createCreditNote}
                generatePartialCreditNote={() => {
                    onClose();
                    onOpenCreditNotePartial();
                }}
            />
            <CreditNotePartialModal
                isOpen={isOpenCreditNotePartial}
                onClose={onCloseCreditNotePartial}
                generateCreditNote={(amount: number, vat: number) => createCreditNote(amount, vat)}
            />
            <MarkAsPaidModal
                isOpen={isOpenMarkAsPaid}
                onClose={() => OnCloseMarkAsPaid()}
                invoice={invoiceToMarkAsPaid}
                onClick={addInvoiceTransaction}
                onTransactionChange={onInvoiceUpdated}
            />
            <EmailsHistoryModal
                isOpen={isOpenEmailsHistory}
                onClose={() => OnCloseEmailHistory()}
                invoice={invoiceToMarkAsPaid}
                onClick={OnCloseEmailHistory}
            />
            <Card extra="mt-1 w-full px-6 pb-6 sm:overflow-x-auto border">
                {/* Header */}
                <div className="relative flex flex-wrap items-center justify-between gap-3 pt-5 pb-1">
                    <div className="flex items-center gap-3">
                        <div className="text-xl font-bold text-navy-700 dark:text-white">
                            Factures prioritaires
                        </div>
                        {invoicesMeta?.total > 0 && (
                            <span className="rounded-full bg-lightPrimary px-2.5 py-0.5 text-xs font-semibold text-toola-500">
                                {invoicesMeta.total}
                            </span>
                        )}
                    </div>
                    <div className="flex flex-wrap items-center gap-2">
                        <div className="flex items-center gap-1.5 rounded-xl border border-gray-200 px-3 py-1.5 transition-colors focus-within:border-toola-500 dark:border-white/20 dark:bg-navy-800">
                            <label className="select-none whitespace-nowrap text-xs text-gray-400 dark:text-gray-500">
                                Du
                            </label>
                            <input
                                type="date"
                                value={dateFrom}
                                onChange={(e) => handleDateFromChange(e.target.value)}
                                className="text-sm text-navy-700 focus:outline-none dark:bg-navy-800 dark:text-white"
                            />
                        </div>
                        <div className="flex items-center gap-1.5 rounded-xl border border-gray-200 px-3 py-1.5 transition-colors focus-within:border-toola-500 dark:border-white/20 dark:bg-navy-800">
                            <label className="select-none whitespace-nowrap text-xs text-gray-400 dark:text-gray-500">
                                Au
                            </label>
                            <input
                                type="date"
                                value={dateTo}
                                onChange={(e) => handleDateToChange(e.target.value)}
                                className="text-sm text-navy-700 focus:outline-none dark:bg-navy-800 dark:text-white"
                            />
                        </div>
                        <input
                            type="text"
                            placeholder="Rechercher un client..."
                            value={search}
                            onChange={(e) => handleSearchChange(e.target.value)}
                            className="rounded-xl border border-gray-200 px-3 py-1.5 text-sm text-navy-700 placeholder-gray-300 transition-colors focus:border-toola-500 focus:outline-none dark:border-white/20 dark:bg-navy-800 dark:text-white"
                        />
                    </div>
                </div>

                {/* Table */}
                <div className="mt-3 overflow-x-auto xl:overflow-x-hidden">
                    <table className="w-full">
                        <thead>
                            {table.getHeaderGroups().map((headerGroup) => (
                                <tr key={headerGroup.id}>
                                    {headerGroup.headers.map((header) => (
                                        <th
                                            key={header.id}
                                            colSpan={header.colSpan}
                                            onClick={header.column.getToggleSortingHandler()}
                                            className="cursor-pointer border-b border-gray-100 pb-3 pr-4 text-start dark:border-white/10"
                                        >
                                            {flexRender(
                                                header.column.columnDef.header,
                                                header.getContext(),
                                            )}
                                        </th>
                                    ))}
                                </tr>
                            ))}
                        </thead>
                        <tbody>
                            {table.getRowModel().rows.map((row) => {
                                const diffDays = getDiffDays(row.original.dueDate);
                                const firstTdBorder =
                                    diffDays > 30
                                        ? 'border-l-4 border-red-400'
                                        : diffDays > 0
                                            ? 'border-l-4 border-orange-400'
                                            : 'border-l-4 border-green-400';

                                return (
                                    <tr
                                        key={row.id}
                                        className="group border-b border-gray-50 transition-colors duration-100 last:border-0 hover:bg-lightPrimary/50 dark:border-white/5 dark:hover:bg-navy-700/50"
                                    >
                                        {row.getVisibleCells().map((cell, cellIndex) => (
                                            <td
                                                key={cell.id}
                                                className={`py-3 pr-4 ${
                                                    cellIndex === 0
                                                        ? `pl-2 ${firstTdBorder}`
                                                        : ''
                                                }`}
                                            >
                                                {flexRender(
                                                    cell.column.columnDef.cell,
                                                    cell.getContext(),
                                                )}
                                            </td>
                                        ))}
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>

                    {/* Empty state */}
                    {table.getRowModel().rows.length === 0 && (
                        <div className="col-span-12 mt-10 mb-4 animate-fadeIn">
                            <img
                                className="mx-auto h-24 w-24 opacity-30"
                                src={invoiceImg}
                                alt=""
                            />
                            <h4 className="mt-4 text-center text-lg font-semibold text-navy-700 dark:text-white">
                                Aucune facture disponible
                            </h4>
                            <p className="mt-1 text-center text-sm text-gray-400">
                                Les filtres appliqués ne correspondent à aucune facture existante.
                            </p>
                        </div>
                    )}
                </div>
            </Card>
        </>
    );
}
